"use client"

import { ColumnDef } from "@tanstack/react-table"
import { Tag } from "~/ui/tag"
import { InvitationActionsDropdown } from "./invitation-actions-dropdown"
import { InvitationStatus } from "~/__generated__/graphql"

export type AdminInvitationColumns = {
  id: string
  title: string
  token: string
  status: InvitationStatus
  contactsAddedCount: number
  createdAt: string
  inviteUrl: string
  user: {
    id: string
    fullName: string
    handle?: string | null
  }
  tags: Array<{
    id: string
    name: string
  }>
}

export const adminInvitationColumns: ColumnDef<AdminInvitationColumns | null | undefined>[] = [
  {
    accessorKey: "title",
    header: "Title",
    cell: ({ row }) => {
      const invitation = row.original
      if (!invitation) return null

      return (
        <div className="max-w-xs">
          <div className="truncate font-medium">{invitation.title}</div>
        </div>
      )
    },
  },
  {
    accessorKey: "inviteUrl",
    header: "Invite Link",
    cell: ({ row }) => {
      const invitation = row.original
      if (!invitation) return null

      return (
        <div className="max-w-xs">
          <div className="truncate text-sm text-muted-foreground">{invitation.inviteUrl}</div>
          <div className="text-xs text-gray-400">
            by {invitation.user.fullName}
            {invitation.user.handle && ` (@${invitation.user.handle})`}
          </div>
        </div>
      )
    },
  },
  {
    accessorKey: "tags",
    header: "Tags",
    cell: ({ row }) => {
      const invitation = row.original
      if (!invitation) return null

      return (
        <div className="flex flex-wrap gap-1">
          {invitation.tags.length > 0 ? (
            invitation.tags.map((tag) => (
              <Tag key={tag.id} selected={false}>
                {tag.name}
              </Tag>
            ))
          ) : (
            <span className="text-sm text-gray-400">No tags</span>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: "contactsAddedCount",
    header: "Contacts Added",
    cell: ({ row }) => {
      const count = row.getValue("contactsAddedCount") as number
      return (
        <div className="text-center">
          <span className="font-medium">{count}</span>
        </div>
      )
    },
  },
  {
    accessorKey: "createdAt",
    header: "Date Created",
    cell: ({ row }) => {
      const date = new Date(row.getValue("createdAt"))
      return (
        <div>
          <div className="text-sm">
            {date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
          <div className="text-xs text-muted-foreground">
            {date.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })}
          </div>
        </div>
      )
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const invitation = row.original
      if (!invitation) return null

      return (
        <div className="flex items-center justify-end">
          <InvitationActionsDropdown
            invitationId={invitation.id}
            currentStatus={invitation.status}
          />
        </div>
      )
    },
  },
]
